// src/services/utils.js

// Environment detection
const isDevelopment = import.meta.env.DEV;
const isTest = import.meta.env.MODE === 'test';

// Global flags to control logging in test environment
// These can be set in specific tests when more verbose logging is needed
if (isTest && typeof window !== 'undefined') {
  window.__LOG_CONFIG = {
    ENABLE_INFO_LOGS: false,
    ENABLE_DEBUG_LOGS: false,
    ENABLE_ERROR_LOGS: false
  };
}

/**
 * Centralized logging utility with different log levels
 * Logs are suppressed during tests by default to keep output clean
 */
export const logger = {
  info: (context, message, data) => {
    if (!isTest || window.__LOG_CONFIG?.ENABLE_INFO_LOGS) {
      console.log(`[${context}]`, message, data || '');
    }
  },

  debug: (context, message, data) => {
    if ((isDevelopment && !isTest) || (isTest && window.__LOG_CONFIG?.ENABLE_DEBUG_LOGS)) {
      console.log(`[${context}][DEBUG]`, message, data || '');
    }
  },

  error: (context, message, error) => {
    if (!isTest || window.__LOG_CONFIG?.ENABLE_ERROR_LOGS) {
      console.error(`[${context}][ERROR]`, message, error || '');
    }
  }
};

/**
 * Get CSRF token from cookie
 * Django requires this token for POST/PUT/PATCH/DELETE requests
 */
export const getCsrfToken = () => {
  const name = 'csrftoken=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');

  for (let cookie of cookieArray) {
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length, cookie.length);
    }
  }
  return '';
};

/**
 * Fetch a fresh CSRF token from the server
 * This sets the csrftoken cookie which can then be read by getCsrfToken()
 * Call this before making auth requests to ensure we have a valid token
 */
export const fetchCsrfToken = async () => {
  try {
    await fetch('/accounts/api/csrf/', {
      method: 'GET',
      credentials: 'include'
    });
    logger.debug('Auth', 'Fetched CSRF token');
    return true;
  } catch (error) {
    logger.error('Auth', 'Failed to fetch CSRF token', error);
    return false;
  }
};

/**
 * Gets the API base URL for chatroom endpoints
 */
export const getApiUrl = () => {
  // Both dev (via proxy) and prod use /api prefix
  return '/api';
};

/**
 * Convert a string to a URL-safe slug
 */
export const slugify = (text) => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')  // Remove non-word chars
    .replace(/[\s_-]+/g, '-')   // Replace spaces/underscores with hyphens
    .replace(/^-+|-+$/g, '');   // Trim hyphens from ends
};

/**
 * Generate a channel URL path
 */
export const getChannelPath = (spaceName, channelName) => {
  return `/${slugify(spaceName)}/${slugify(channelName)}`;
};

/**
 * Calculates WebSocket URL based on current environment
 */
export const getWebSocketUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = import.meta.env.PROD ? window.location.host : 'localhost:8000';
  return `${protocol}//${host}/ws/chatrooms/`;
};
